import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

const TOKEN_KEY = "mc_jwt";
const USER_KEY = "mc_user";
const BASE = process.env.REACT_APP_BACKEND_URL;
export const API_BASE = `${BASE}/api`;

const AuthContext = createContext(null);

export async function apiFetch(path, init = {}, token) {
  const headers = new Headers(init.headers || {});
  if (!headers.has("Content-Type") && init.body) headers.set("Content-Type", "application/json");
  if (token) headers.set("Authorization", `Bearer ${token}`);
  return fetch(`${API_BASE}${path}`, { ...init, headers });
}

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);

  const load = useCallback(async () => {
    const t = localStorage.getItem(TOKEN_KEY);
    if (t) {
      setToken(t);
      const raw = localStorage.getItem(USER_KEY);
      if (raw) {
        try { setUser(JSON.parse(raw)); } catch {}
      }
      try {
        const r = await apiFetch("/auth/me", { method: "GET" }, t);
        if (r.ok) {
          const data = await r.json();
          setUser(data.user);
          localStorage.setItem(USER_KEY, JSON.stringify(data.user));
          // Sliding session — backend rotates the JWT on every /auth/me.
          // Swap in the fresh one so we reset the 30-day inactivity clock.
          if (data.token) {
            localStorage.setItem(TOKEN_KEY, data.token);
            setToken(data.token);
          }
        } else if (r.status === 401) {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
          setToken(null);
          setUser(null);
        }
      } catch {}
    }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const setSession = useCallback(async (t, u) => {
    localStorage.setItem(TOKEN_KEY, t);
    localStorage.setItem(USER_KEY, JSON.stringify(u));
    setToken(t);
    setUser(u);
  }, []);

  const refresh = useCallback(async () => {
    if (!token) return;
    const r = await apiFetch("/auth/me", { method: "GET" }, token);
    if (r.ok) {
      const data = await r.json();
      setUser(data.user);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
      if (data.token) {
        localStorage.setItem(TOKEN_KEY, data.token);
        setToken(data.token);
      }
    }
  }, [token]);

  const signOut = useCallback(async () => {
    try { if (token) await apiFetch("/auth/logout", { method: "POST" }, token); } catch {}
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    setToken(null);
    setUser(null);
  }, [token]);

  const value = useMemo(() => ({ loading, user, token, setSession, refresh, signOut }),
    [loading, user, token, setSession, refresh, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}

export function useApi() {
  const { token } = useAuth();
  return useCallback((path, init) => apiFetch(path, init, token), [token]);
}
